import type { OvertimeRequestDTO } from "@/modules/hr/domain/contracts";
import { formatDateLocal } from "@/shared/lib/date-time";
import {
  Badge,
  Button,
  Card,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { Eye } from "lucide-react";

const STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຖ້າການອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

const STATUS_VARIANTS: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  approved: "default",
  pending: "secondary",
  rejected: "destructive",
};

type OvertimeRequestTableProps = {
  data: OvertimeRequestDTO[];
  isLoading?: boolean;
  totalCount: number;
  offset: number;
  limit: number;
  onPageChange?: (offset: number, limit: number) => void;
  statusFilter?: string;
  onStatusFilterChange?: (status: string | undefined) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange?: (id: string, desc: boolean) => void;
};

export function OvertimeRequestTable({
  data,
  isLoading,
  totalCount,
  offset,
  limit,
  onPageChange,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  sortOrder,
  onSortingChange,
}: OvertimeRequestTableProps) {
  const navigate = useNavigate({ from: "/app/hr/overtime/requests" });

  const columns: TanstackReactTable.ColumnDef<OvertimeRequestDTO>[] = [
    createSortableColumn<OvertimeRequestDTO>("workDate", "ວັນທີ", {
      size: 120,
      cell: ({ row }) => formatDateLocal(row.original.workDate),
    }),
    createSortableColumn<OvertimeRequestDTO>("startTime", "ເວລາ", {
      size: 140,
      cell: ({ row }) => `${row.original.startTime} – ${row.original.endTime}`,
    }),
    createSortableColumn<OvertimeRequestDTO>("hours", "ຊົ່ວໂມງ", {
      size: 120,
      cell: ({ row }) => `${row.original.hours} ຊົ່ວໂມງ`,
    }),
    createSortableColumn<OvertimeRequestDTO>("status", "ສະຖານະ", {
      size: 140,
      cell: ({ row }) => (
        <Badge variant={STATUS_VARIANTS[row.original.status] ?? "outline"}>
          {STATUS_LABELS[row.original.status] ?? row.original.status}
        </Badge>
      ),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() =>
            navigate({
              to: "/app/hr/overtime/requests/$id",
              params: { id: row.original.id },
            })
          }
          aria-label="ເບິ່ງລາຍລະອຽດ"
        >
          <Eye className="size-4" />
        </Button>
      ),
      size: 50,
    },
  ];

  return (
    <Card className="gap-0 p-0 pt-4">
      {onStatusFilterChange && (
        <div className="flex flex-wrap gap-2 border-b px-4 pb-4">
          {["all", "pending", "approved", "rejected"].map((s) => (
            <Button
              key={s}
              type="button"
              variant={
                (s === "all" && !statusFilter) || statusFilter === s
                  ? "default"
                  : "secondary"
              }
              size="sm"
              className={
                (s === "all" && !statusFilter) || statusFilter === s
                  ? "bg-black text-white hover:bg-black/90"
                  : ""
              }
              onClick={() => onStatusFilterChange(s === "all" ? undefined : s)}
            >
              {s === "all" ? "ທັງໝົດ" : (STATUS_LABELS[s] ?? s)}
            </Button>
          ))}
        </div>
      )}
      <DataTable<OvertimeRequestDTO, unknown>
        noDataMessage="ຍັງບໍ່ມີຄຳຂໍ OT"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={
          onPageChange ? (p) => onPageChange(p.offset, p.limit) : undefined
        }
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (!onSortingChange) return;
          if (!sorting || sorting.length === 0) return;
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id ?? "", !!sorting[0]?.desc);
        }}
      />
    </Card>
  );
}
